<template>
  <div class="ingredient-list-wrapper">
    <BaseErrors :errors="errors"></BaseErrors>

    <ul class="ingredients-list">
      <li v-if="!haveIngredients">No ingredients added</li>
      <li v-for="item in recipe.ingredientSchedules"
          :data-id="item.id"
          @click.stop.prevent="updateIngredient" data-type="ingredient"><!-- Ingredients Schedule -->

        <RecipeShowListItem
          :item="item"
          :name="item.name"
          :sub-text="[item.country, item.producer, item.variant] | joinedList"
          :amount="item.amount | grainWeight"
          type="ingredient"
          :editable="true"
          :badges="[
            `${item.percentage}%`,
            `GP ${item.gravityPoints}`,
            // `Color ${item.color}`
          ]"
          icon-name="grain-icon"
          view-box="0 0 100 100"
          v-on:delete-item="removeIngredient"
        />
      </li> <!--/ ingredients Schedule -->

      <li v-for="item in recipe.hopSchedules"
          :data-id="item.id"
          @click.stop.prevent="updateIngredient" data-type="hop"><!-- Hop Schedule -->

        <RecipeShowListItem
          :item="item"
          :name="`${item.name} | ${item.use} ${item.hopUseId === 3 ? item.time[2].val : item.time[0].val } ${item.hopUseId === 3 ? item.time[2].unit : item.time[0].unit }`"
          :sub-text="[item.origin, item.producer] | joinedList"
          :amount="item.amount | hopsWeight"
          type="hop"
          :editable="true"
          :badges="[
            `${item.form}`,
            `AA ${item.aa}%`,
            `IBU ${item.ibu}`
          ]"
          icon-name="hop-icon"
          viewBox="0 0 60 70"
          v-on:delete-item="removeIngredient"
        />
      </li><!--/ Hop Schedule -->

      <li v-for="item in recipe.otherIngredientSchedules"
          :data-id="item.id"
          @click.stop.prevent="updateIngredient" data-type="otherIngredient"><!-- Other Schedule -->

        <RecipeShowListItem
          :item="item"
          :name="`${item.name} | ${item.use} ${item.time[0].val} ${item.time[0].unit}`"
          :sub-text="[item.origin, item.producer] | joinedList"
          :amount="item.amount | hopsWeight"
          type="other-ingredient"
          :editable="true"
          :badges="[
            `${item.type}`,
          ]"
          icon-name="add-circle"
          view-box="0 0 24 24"
          v-on:delete-item="removeIngredient"
        />
      </li><!--/ Other Schedule -->

      <li v-for="item in recipe.yeastSchedules"
          :data-id="item.id"
          @click.stop.prevent="updateIngredient" data-type="yeast"><!-- Yeast Schedule -->

        <RecipeShowListItem
          :item="item"
          :name="`${item.code} / ${item.name}`"
          :sub-text="`${item.laboratory}`"
          :amount="'1 pk'"
          type="yeast"
          :editable="true"
          :badges="[
            `${item.attenuation}%`,
          ]"
          icon-name="yeast-icon"
          view-box="0 0 100 100"
          v-on:delete-item="removeIngredient"
        />
      </li><!--/ Yeast Schedule -->
    </ul>

    <div class="actions">
        <a href="" class="add-item" @click.prevent="showFermentableModal = 'create'">
          <BaseIcon name="add-icon"
                    width="20"
                    height="20"></BaseIcon>
          Fermentable
        </a>
        <a href="" class="add-item" @click.prevent="showHopModal = 'create'">
          <BaseIcon name="add-icon"
                    width="20"
                    height="20"></BaseIcon>
          Hop
        </a>
        <a href="" class="add-item" @click.prevent="showYeastModal = 'create'">
          <BaseIcon name="add-icon"
                    width="20"
                    height="20"></BaseIcon>
          Yeast
        </a>
        <a href="" class="add-item" @click.prevent="showMiscModal = 'create'">
          <BaseIcon name="add-icon"
                    width="20"
                    height="20"></BaseIcon>
          Misc
        </a>
    </div>

    <FermentablesModal :recipeUrl="recipeUrl"
                       :recipe="recipe"
                       :scheduleId="scheduleId"
                       v-on:close-modal="showFermentableModal = null"
                       :action="showFermentableModal"
                       v-if="showFermentableModal">
    </FermentablesModal>

    <YeastsModal :recipeUrl="recipeUrl"
                 :recipe="recipe"
                 :scheduleId="scheduleId"
                 v-on:close-modal="showYeastModal = null"
                 :action="showYeastModal"
                 v-if="showYeastModal">
    </YeastsModal>

    <MiscModal :recipeUrl="recipeUrl"
               :recipe="recipe"
               :scheduleId="scheduleId"
               v-on:close-modal="showMiscModal = null"
               :action="showMiscModal"
               v-if="showMiscModal">
    </MiscModal>

    <HopsModal :recipeUrl="recipeUrl"
               :recipe="recipe"
               :scheduleId="scheduleId"
               v-on:close-modal="showHopModal = null"
               :action="showHopModal"
               v-if="showHopModal">
    </HopsModal>
  </div>
</template>

<script>
  import { EventBus } from '../lib/event-bus.js';
  import FermentablesModal from "./modals/FermentablesModal";
  import RecipeShowListItem from "./bits/RecipeShowListItem";
  import YeastsModal from "./modals/YeastsModal";
  import MiscModal from "./modals/MiscModal";
  import HopsModal from "./modals/HopsModal";
  import { HTTP } from '../lib/http';

  export default {
    props: {
      recipe: {
        type: Object
      },
      recipeUrl: {
        type: String
      }
    },
    components: {
      FermentablesModal,
      RecipeShowListItem,
      YeastsModal,
      MiscModal,
      HopsModal
    },
    data() {
        return {
            recipeId: null,
            massUnit: null,
            hopUnit: null,
            showFermentableModal: null,
            showYeastModal: false,
            showHopModal: false,
            showMiscModal: false,
            errors: null,
            nothingAdded: true,
            loading: false,
            scheduleId: null
        };
    },
    computed: {
        haveIngredients() {
            return (this.recipe.ingredientSchedules.length +
                this.recipe.hopSchedules.length +
                this.recipe.otherIngredientSchedules.length +
                this.recipe.yeastSchedules.length) > 0;
        },
    },
    methods: {
      updateIngredient: function (e) {
        this.scheduleId = e.currentTarget.getAttribute('data-id');
        const itemType = e.currentTarget.getAttribute('data-type');

        switch(itemType) {
            case 'ingredient':
                this.showFermentableModal = 'update';
                break;
            case 'hop':
                this.showHopModal = 'update';
                break;
            case 'otherIngredient':
                this.showMiscModal = 'update';
                break;
            case 'yeast':
                this.showYeastModal = 'update';
                break;
            default:
            // code block
        }
      },
      removeIngredient: function (e) {
          if (confirm('Are you sure?')) {
              const itemId = e.currentTarget.getAttribute('data-id');
              const itemType = e.currentTarget.getAttribute('data-type');
              let url = `${this.recipeUrl}/${itemType}-schedules/${itemId}`;

              this.errors = [];

              HTTP.delete(url)
                  .then(response => {
                      EventBus.$emit('reloadRecipe', true);
                  })
                  .catch(err => {
                      this.errors = [err.response.data.errors];
                  })
          }
      }
    },
    beforeMount() {
      this.massUnit = localStorage.massUnit;
      this.hopUnit = localStorage.hopUnit;
      EventBus.$on('massUnitChanged', val => {
        this.massUnit = val;
      });

      EventBus.$on('AddFermentableChanged', val => {
        this.showAddFermentable = val;
      });

      EventBus.$on('hopUnitChanged', val => {
        this.hopUnit = val;
      });
    },
  };
</script>

<style scoped lang="scss">
  @import "/../assets/scss/_variablesx.scss";

  $gray-base: lighten($gray-900, 15);

  .ingredient-list-wrapper {
    position: relative;
  }

  .ingredients-list {
    padding: 0;
    margin: 0;
    list-style-type: none;
    font-weight: 400;

    li {
      padding: 0;
      position: relative;
      align-items: center;
      border-top: 1px solid;

      &:first-child {
        border-top: none;
      }
      ::v-deep svg {
        fill: $gray-base;
      }
    }
  }

  .actions {
    text-align: right;
    margin: 1em 0;
    @media (max-width: 575.98px) {
      text-align: center;
      display: flex;
      justify-content: space-evenly;
    }

    .add-item {
      font-size: .8em;
      margin-left: .5em;
      color: $green;
      white-space: nowrap;
      display: inline-block;
      transition: all 200ms;
      border: 1px solid;
      border-radius: 6px;
      padding: .3em .5em;

      &:first-child {
        margin-left: 0;
      }

      ::v-deep svg {
        transition: all 200ms;
        fill: $green;
      }
      &:hover {
        text-decoration: none;
        color: $orange;

        ::v-deep svg {
          fill: orange;
        }
      }
    }
  }

  .modal-mask {
    position: fixed;
    z-index: 9998;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, .5);
    display: table;
    transition: opacity .3s ease;
  }

  .modal-wrapper {
    display: table-cell;
    vertical-align: middle;
  }

</style>
